import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { docChon, ghiChon, locVaDem } from "@shared/locMuc.js";
import { label as taxonomyLabel } from "@shared/taxonomy.js";
import { LOAI_CHUONG_TRINH, LOAI_HO_TRO } from "../lib/programModel.js";
import { normalize } from "../lib/workspace.js";
import { KhungHaiCot, NhomLoc, OTim, DongDem } from "../components/LocHaiCot.jsx";
import { useChuongTrinh } from "../lib/programs.js";
import { phanNhom, TRANG_THAI } from "@shared/programs.js";
import { homNay as layHomNay } from "../lib/format.js";
import { tomTatAlumniText } from "../lib/fundLabels.js";
import { tomTatDanhMuc } from "../lib/fundModel.js";

/**
 * Trang danh mục chương trình.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * VÌ SAO TRẠNG THÁI CHỈ TÍNH SAU KHI CHẠY TRÊN TRÌNH DUYỆT
 *
 * "Còn 17 ngày" phụ thuộc ngày hôm nay. Nếu render sẵn lúc build, con số đó
 * đóng băng tại ngày build và sẽ sai ngay hôm sau — tệ hơn là không có gì, vì
 * người đọc tin vào một con số cũ.
 *
 * Nên phần nội dung (tên, mô tả, điều kiện) được render sẵn để Google đọc
 * được, còn nhãn trạng thái và số ngày còn lại chỉ xuất hiện sau khi trang
 * chạy trên máy người dùng.
 * ─────────────────────────────────────────────────────────────────────────
 */

const MAU_TRANG_THAI = {
  [TRANG_THAI.DANG_MO]: "var(--la)",
  [TRANG_THAI.LIEN_TUC]: "var(--la)",
  [TRANG_THAI.SAP_MO]: "var(--ho-phach)",
  [TRANG_THAI.CHUA_RO]: "var(--chu-mo)",
  [TRANG_THAI.DA_DONG]: "var(--chu-mo)",
};

/**
 * "Doanh nghiệp đã qua chương trình" — bằng chứng nhanh nhất về việc chương
 * trình này hợp với ai. Cố ý gọi là "đã tham gia" chứ không phải "đã đầu tư":
 * phần lớn chương trình ở đây cấp tín dụng, cố vấn hoặc giải thưởng, không rót
 * vốn đổi cổ phần. Nói nhầm chỗ này là hứa hộ người khác một điều họ không hứa.
 *
 * Dòng tóm tắt suy từ chính danh sách bên dưới nên không bao giờ lệch dữ liệu.
 */
const NHAN_DA_THAM_GIA = {
  RESEARCH_GRANT: { tieuDe: "Dự án đã tài trợ", cumTu: "dự án đã được tài trợ" },
  MATCHING_GRANT: { tieuDe: "Dự án đã tài trợ", cumTu: "dự án đã được tài trợ" },
  BLENDED_IMPACT: { tieuDe: "Đã tài trợ và đầu tư", cumTu: "tổ chức đã nhận vốn" },
};
const NHAN_MAC_DINH = {
  tieuDe: "Doanh nghiệp đã qua chương trình",
  cumTu: "doanh nghiệp đã qua chương trình",
};

function DaThamGia({ ct }) {
  const al = ct.alumni;
  if (!al?.items?.length) return null;
  const nhan = NHAN_DA_THAM_GIA[ct.kind] || NHAN_MAC_DINH;
  const cau = tomTatAlumniText(tomTatDanhMuc(ct), nhan.cumTu);
  return (
    <>
      <h3>{nhan.tieuDe}</h3>
      {cau && <p className="ct-alum-tom-tat">{cau}</p>}
      <ul className="ct-alum">
        {al.items.map((it, i) => (
          <li key={`${it.name}-${i}`}>
            <span className="ct-alum-ten">{it.name}</span>
            {it.roundDate && <span className="ct-alum-nam">{it.roundDate}</span>}
            {it.whatTheyDo && <span className="ct-alum-lam">{it.whatTheyDo}</span>}
          </li>
        ))}
      </ul>
      <p className="ct-alum-ghi">
        Danh sách chỉ nêu những doanh nghiệp được công bố công khai.
        {al.note ? ` ${al.note}` : ""}
        {al.verifiedAt ? ` Kiểm chứng ngày ${al.verifiedAt}.` : ""}
        {al.source ? (
          <>
            {" "}
            <a href={al.source} target="_blank" rel="noreferrer">
              Nguồn của chương trình ↗
            </a>
          </>
        ) : null}
      </p>
    </>
  );
}

const nhanLoai = (k) => LOAI_CHUONG_TRINH.find((x) => x.value === k)?.label || k;
const tenTax = (domain, v) => { try { return taxonomyLabel(domain, v) || v; } catch { return v; } };
const THU_TU_GD = ["IDEA", "PROTOTYPE", "ANGEL", "PRESEED", "SEED", "SEED_PLUS", "SERIES_A", "LATER"];

/** Một chương trình là một dòng ngang; điều kiện, doanh nghiệp đã tham gia và nguồn mở ra tại chỗ. */
function The({ ct }) {
  const giaiDoan = (ct.eligibility?.stages || []).map((x) => tenTax("STAGE", x));
  const coChiTiet = ct.eligibility?.hardRequirements?.length || ct.alumni?.items?.length || ct.provenance?.sources?.length;
  return (
    <article className="ctd" id={ct.slug || ct.programId}>
      <div className="ctd-logo" aria-hidden="true">
        {ct.logoUrl ? <img src={ct.logoUrl} alt="" width="48" height="48" loading="lazy" /> : <span>{(ct.shortName || ct.name).slice(0, 2).toUpperCase()}</span>}
      </div>
      <div className="ctd-than">
        <div className="ctd-dau">
          <h3 className="ctd-ten">{ct.name}</h3>
          {ct.nhanTrangThai && (
            <span className="ctd-tt" style={{ "--mau": MAU_TRANG_THAI[ct.trangThai] }}>
              <i aria-hidden="true" />{ct.nhanTrangThai}{ct.soNgayConLai != null && `, còn ${ct.soNgayConLai} ngày`}
            </span>
          )}
        </div>
        <p className="ctd-meta">
          {ct.organizer}
          <span>{nhanLoai(ct.kind)}</span>
          {giaiDoan.length > 0 && <span>{giaiDoan.join(", ")}</span>}
        </p>
        {ct.content?.summary && <p className="ctd-tom-tat">{ct.content.summary}</p>}
        {ct.benefit?.note && <p className="ctd-loi-ich">{ct.benefit.note}</p>}
        {coChiTiet ? (
          <details className="ctd-chi-tiet">
            <summary>Điều kiện, doanh nghiệp đã tham gia và nguồn</summary>
            {ct.eligibility?.hardRequirements?.length > 0 && (
              <>
                <h3>Điều kiện</h3>
                <ul>{ct.eligibility.hardRequirements.map((r) => <li key={r}>{r}</li>)}</ul>
              </>
            )}
            <DaThamGia ct={ct} />
            {ct.provenance?.sources?.length > 0 && (
              <>
                <h3>Nguồn thông tin</h3>
                {ct.provenance.sources.map((x, i) => (
                  <p key={i}><a href={x.url} target="_blank" rel="noreferrer">{x.note || "Nguồn chính thức"} ↗</a></p>
                ))}
              </>
            )}
          </details>
        ) : null}
        <div className="ctd-chan">
          <span className="ctd-kiem">Kiểm chứng: {ct.provenance?.lastVerifiedAt || "Chưa xác minh"}</span>
          <a className="ctd-nop" href={ct.process.applyUrl} target="_blank" rel="noopener noreferrer">Xem hướng dẫn chính thức ↗</a>
        </div>
      </div>
    </article>
  );
}

export default function Programs() {
  // Bản build hiện ngay, rồi thay bằng dữ liệu mới nhất trên Firestore.
  const PROGRAMS = useChuongTrinh();
  // null cho tới khi chạy trên trình duyệt — xem ghi chú đầu file.
  const [homNay, setHomNay] = useState(null);
  useEffect(() => {
    setHomNay(layHomNay());
  }, []);

  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const chon = {
    tt: docChon(params, "tt"), kind: docChon(params, "kind"), ho_tro: docChon(params, "ho_tro"),
    stage: docChon(params, "stage"), nganh: docChon(params, "nganh"), geo: docChon(params, "geo"),
  };
  const doiNhom = (k) => (ds) => setParams(ghiChon(params, k, ds), { replace: true });
  // Trạng thái chỉ có sau khi chạy trên trình duyệt (xem ghi chú đầu file).
  const ds = homNay ? phanNhom(PROGRAMS, homNay) : null;
  const tatCa = ds ? [...ds.dangMo, ...ds.theoDoi] : PROGRAMS;
  const ttCua = (ct) => (ct.trangThai === TRANG_THAI.LIEN_TUC ? TRANG_THAI.DANG_MO : ct.trangThai);
  const { ketQua, dem } = locVaDem(
    tatCa,
    [
      { key: "tt", giaTri: (ct) => (ct.trangThai ? [ttCua(ct)] : []) },
      { key: "kind", giaTri: (ct) => [ct.kind].filter(Boolean) },
      { key: "ho_tro", giaTri: (ct) => ct.benefit?.capitalTypes || [] },
      { key: "stage", giaTri: (ct) => ct.eligibility?.stages || [] },
      { key: "nganh", giaTri: (ct) => ct.eligibility?.industries || [],
        khop: (ct, v) => !(ct.eligibility?.industries || []).length || v.some((x) => ct.eligibility.industries.includes(x)) },
      { key: "geo", giaTri: (ct) => ct.eligibility?.geos || [] },
    ],
    homNay ? chon : { ...chon, tt: [] },
    (ct) => normalize(`${ct.name} ${ct.organizer || ""} ${ct.content?.summary || ""} ${nhanLoai(ct.kind)}`).includes(normalize(q)),
  );
  const cacGiaTri = (f) => [...new Set(tatCa.flatMap(f))];
  const theoSo = (key) => (a, b) => (dem[key][b] || 0) - (dem[key][a] || 0);
  const cacStage = cacGiaTri((ct) => ct.eligibility?.stages || []).sort((a, b) => THU_TU_GD.indexOf(a) - THU_TU_GD.indexOf(b));
  const cacNganh = cacGiaTri((ct) => ct.eligibility?.industries || []).sort(theoSo("nganh"));
  const cacGeo = cacGiaTri((ct) => ct.eligibility?.geos || []);
  const cacLoai = LOAI_CHUONG_TRINH.filter((x) => tatCa.some((ct) => ct.kind === x.value));
  const cacHoTro = LOAI_HO_TRO.filter((x) => tatCa.some((ct) => (ct.benefit?.capitalTypes || []).includes(x.value)));
  const dangLoc = Boolean(q || Object.values(chon).flat().length);
  const dangMo = ketQua.filter((ct) => ttCua(ct) === TRANG_THAI.DANG_MO);
  const theoDoi = ketQua.filter((ct) => ttCua(ct) !== TRANG_THAI.DANG_MO);
  const TT_LOC = [
    { v: TRANG_THAI.DANG_MO, nhan: "Đang nhận hồ sơ" },
    { v: TRANG_THAI.SAP_MO, nhan: "Sắp mở" },
    { v: TRANG_THAI.CHUA_RO, nhan: "Chưa rõ lịch" },
    { v: TRANG_THAI.DA_DONG, nhan: "Đã đóng" },
  ];

  return (
    <>
      <div className="page-heading">
        <p className="eyebrow">NGUỒN LỰC NGOÀI VỐN CỔ PHẦN</p>
        <h1>Chương trình và tài trợ</h1>
        <img className="bia-trang" src="/anh/chuong-trinh-bia.webp" alt="Chương trình hỗ trợ và tài trợ cho startup" width="2400" height="720" fetchpriority="high" decoding="async" />
      </div>
      <p className="ct-gioi-thieu">
        Ngoài quỹ đầu tư, còn có chương trình tăng tốc, cuộc thi, tài trợ nghiên cứu và tín dụng hạ tầng.
        Phần lớn trong số này không lấy cổ phần của bạn. Chương trình mở và đóng theo đợt; mục đã đóng vẫn
        được giữ lại để bạn chuẩn bị cho kỳ sau. <Link to="/nguon-von">Xem quỹ đầu tư →</Link>
      </p>

      <KhungHaiCot
        soDangChon={Object.values(chon).flat().length}
        boLoc={<>
          {homNay && (
            <NhomLoc tieuDe="Tình trạng" chon={chon.tt} onDoi={doiNhom("tt")}
              ds={TT_LOC.map((x) => ({ ...x, dem: dem.tt[x.v] || 0, mau: MAU_TRANG_THAI[x.v] }))} />
          )}
          <NhomLoc tieuDe="Loại chương trình" chon={chon.kind} onDoi={doiNhom("kind")} gioiHan={10}
            ds={cacLoai.map((x) => ({ v: x.value, nhan: x.label, dem: dem.kind[x.value] || 0 }))} />
          <NhomLoc tieuDe="Hình thức hỗ trợ" chon={chon.ho_tro} onDoi={doiNhom("ho_tro")}
            ds={cacHoTro.map((x) => ({ v: x.value, nhan: x.label, dem: dem.ho_tro[x.value] || 0 }))} />
          <NhomLoc tieuDe="Giai đoạn" chon={chon.stage} onDoi={doiNhom("stage")} gioiHan={8}
            ds={cacStage.map((x) => ({ v: x, nhan: tenTax("STAGE", x), dem: dem.stage[x] || 0 }))} />
          <NhomLoc tieuDe="Ngành" chon={chon.nganh} onDoi={doiNhom("nganh")} gioiHan={6} moSan={chon.nganh.length > 0}
            ds={cacNganh.map((x) => ({ v: x, nhan: tenTax("INDUSTRY", x), dem: dem.nganh[x] || 0 }))} />
          <NhomLoc tieuDe="Địa bàn" chon={chon.geo} onDoi={doiNhom("geo")} moSan={chon.geo.length > 0}
            ds={cacGeo.map((x) => ({ v: x, nhan: tenTax("GEO", x), dem: dem.geo[x] || 0 }))} />
        </>}
      >
        <OTim id="ct-tim" nhan="Tìm chương trình" value={q} onChange={(v) => setParams(ghiChon(params, "q", v ? [v] : []), { replace: true })} placeholder="Tìm: AWS, cuộc thi, tài trợ nghiên cứu…" />
        <DongDem dangLoc={dangLoc} onXoa={() => setParams({}, { replace: true })}>
          Hiển thị <b>{ketQua.length}</b> trên {tatCa.length} chương trình
        </DongDem>
        {!ds ? (
          <div className="ctd-ds">{ketQua.map((ct) => <The key={ct.programId} ct={ct} />)}</div>
        ) : (
          <>
            {dangMo.length > 0 && (
              <section className="ctd-nhom" aria-labelledby="ct-dang-mo">
                <h2 id="ct-dang-mo">Đang nhận hồ sơ <span>{dangMo.length}</span></h2>
                <div className="ctd-ds">{dangMo.map((ct) => <The key={ct.programId} ct={ct} />)}</div>
              </section>
            )}
            {theoDoi.length > 0 && (
              <section className="ctd-nhom" aria-labelledby="ct-theo-doi">
                <h2 id="ct-theo-doi">Theo dõi cho kỳ sau <span>{theoDoi.length}</span></h2>
                <p className="ctd-nhom-ghi">Đã đóng hoặc chưa công bố lịch. Đáng xem trước để chuẩn bị hồ sơ.</p>
                <div className="ctd-ds">{theoDoi.map((ct) => <The key={ct.programId} ct={ct} />)}</div>
              </section>
            )}
          </>
        )}
        {!ketQua.length && (
          <div className="empty-state">
            <h2>Chưa có chương trình khớp bộ lọc này</h2>
            <p>Nhiều chương trình không công bố giai đoạn hoặc ngành, nên lọc càng hẹp càng dễ bỏ sót.</p>
            <button type="button" className="nut phu" onClick={() => setParams({}, { replace: true })}>Xoá bộ lọc</button>
          </div>
        )}
        <p className="ctd-ghi-chu">
          Thông tin lấy từ trang chính thức của từng chương trình, kèm ngày kiểm chứng. Hạn nộp có thể thay đổi,
          nên luôn kiểm tra lại trên trang của họ trước khi chuẩn bị hồ sơ.
        </p>
      </KhungHaiCot>
    </>
  );
}
